import { Box, Button } from "@mui/material";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";

/**
 * Shared footer for stepper step editors that have nothing to persist (e.g.
 * Contacts, whose edits save through their own dialogs). Records the given step
 * complete and navigates to the next step in order. Its "Save" button matches
 * the label used by `StepSaveFooter` so every stepper step's primary action
 * reads consistently.
 *
 * Renders nothing when the user cannot edit (read-only customers) or when the
 * submission is frozen (read-only after the snapshot is taken): there is no
 * continue action in either case.
 */
export function StepContinueFooter({
  submissionId,
  step,
}: {
  submissionId: string;
  step: SubmissionStepType;
}) {
  const { isFrozen, canEdit } = useWasteStepper();
  const { touchStep, loading } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep } = useStepperNavigation();

  const handleContinue = useCallback(async () => {
    await touchStep(step, SubmissionStepStatus.Completed);
    goToNextStep(step);
  }, [touchStep, step, goToNextStep]);

  if (isFrozen || !canEdit) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
      <Button variant="contained" loading={loading} onClick={handleContinue}>
        Save
      </Button>
    </Box>
  );
}
